import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { DatePicker } from "../ui/date-picker";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

function PlayerCheckboxList({ players, selectedIds, onToggle }) {
  if (players.length === 0) {
    return (
      <div className="p-4 text-sm text-slate-500 text-center">
        Aggiungi giocatori prima di creare convocazioni.
      </div>
    );
  }
  return players.map((p) => (
    <label
      key={p.id}
      className="flex items-center gap-3 p-2.5 hover:bg-slate-50 border-b border-slate-100 cursor-pointer"
    >
      <Checkbox
        checked={selectedIds.includes(p.id)}
        onCheckedChange={() => onToggle(p.id)}
        data-testid={`conv-player-${p.id}`}
      />
      <span className="h-6 min-w-6 px-1 rounded bg-sky-100 text-sky-700 text-xs font-bold flex items-center justify-center">
        {p.jersey_number ?? "—"}
      </span>
      <span className="text-sm flex-1">
        {p.last_name} {p.first_name}
      </span>
      <span className="text-xs text-slate-500">{p.role}</span>
    </label>
  ));
}

export default function ConvocationFormDialog({
  open,
  onOpenChange,
  form,
  setForm,
  players,
  fields,
  staff = [],
  parents = [],
  editingId,
  onSubmit,
}) {
  const sortedPlayers = useMemo(
    () => [...players].sort((a, b) => (a.jersey_number || 99) - (b.jersey_number || 99)),
    [players]
  );

  // Combined accompagnatori list (staff + parents) — each with a key
  // ``s:{id}`` or ``p:{id}`` so the same id space doesn't clash.
  const accompList = useMemo(() => {
    const out = [];
    for (const s of staff || []) {
      out.push({
        key: `s:${s.id}`,
        kind: "staff",
        id: s.id,
        name: s.name,
        sub: s.role,
        tessera: s.figc_card_number || "",
      });
    }
    for (const g of parents || []) {
      out.push({
        key: `p:${g.id}`,
        kind: "parent",
        id: g.id,
        name: `${g.last_name || ""} ${g.first_name || ""}`.trim(),
        sub: (g.relation || "genitore").toUpperCase(),
        tessera: g.fiscal_code || "",
      });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name, "it"));
  }, [staff, parents]);

  const togglePlayer = (id) => {
    setForm((f) => ({
      ...f,
      player_ids: f.player_ids.includes(id)
        ? f.player_ids.filter((x) => x !== id)
        : [...f.player_ids, id],
    }));
  };

  const toggleAll = () => {
    setForm((f) => ({
      ...f,
      player_ids: f.player_ids.length === players.length ? [] : players.map((p) => p.id),
    }));
  };

  const toggleAccomp = (key) => {
    setForm((f) => {
      const cur = f.accompagnatori_ids || [];
      return {
        ...f,
        accompagnatori_ids: cur.includes(key)
          ? cur.filter((x) => x !== key)
          : [...cur, key],
      };
    });
  };

  const allSelected = form.player_ids.length === players.length && players.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingId ? "Modifica convocazione" : "Nuova convocazione"}
          </DialogTitle>
          <DialogDescription className="sr-only">Crea una nuova convocazione, seleziona campo e giocatori.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Titolo</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Campionato 5° giornata"
              data-testid="conv-title"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Data partita</Label>
              <DatePicker
                value={form.match_date}
                onChange={(v) => setForm({ ...form, match_date: v })}
                data-testid="conv-date"
              />
            </div>
            <div>
              <Label>Avversario</Label>
              <Input
                value={form.opponent}
                onChange={(e) => setForm({ ...form, opponent: e.target.value })}
              />
            </div>
            <div>
              <Label>Ritrovo (ora)</Label>
              <Input
                type="time"
                value={form.meeting_time}
                onChange={(e) => setForm({ ...form, meeting_time: e.target.value })}
              />
            </div>
            <div>
              <Label>Luogo ritrovo</Label>
              <Input
                value={form.meeting_place}
                onChange={(e) => setForm({ ...form, meeting_place: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>Campo / Stadio</Label>
              {fields && fields.length > 0 ? (
                <div className="flex gap-2">
                  <Select
                    value={form.field_id || "__none__"}
                    onValueChange={(v) => {
                      if (v === "__none__") {
                        setForm({ ...form, field_id: "", location: "" });
                        return;
                      }
                      const f = fields.find((x) => x.id === v);
                      setForm({
                        ...form,
                        field_id: v,
                        location: f ? f.name : form.location,
                      });
                    }}
                  >
                    <SelectTrigger className="flex-1" data-testid="conv-field-select">
                      <SelectValue placeholder="Seleziona campo salvato" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Inserisci manualmente —</SelectItem>
                      {fields.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.name}
                          {f.address && ` · ${f.address}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              <Input
                className="mt-2"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value, field_id: "" })}
                placeholder="Nome del campo (anche manuale)"
              />
            </div>
            <div className="col-span-2">
              <Label>Note</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Manifestazione / Torneo</Label>
              <Input
                value={form.manifestazione || ""}
                onChange={(e) =>
                  setForm({ ...form, manifestazione: e.target.value })
                }
                placeholder="Torneo Sotto le Stelle"
                data-testid="conv-manifestazione"
              />
            </div>
            <div>
              <Label>Girone</Label>
              <Input
                value={form.girone || ""}
                onChange={(e) => setForm({ ...form, girone: e.target.value })}
                placeholder="A / PR / Eliminatorie"
                data-testid="conv-girone"
              />
            </div>
            <div>
              <Label>Ruolo squadra</Label>
              <Select
                value={form.home_or_away || "home"}
                onValueChange={(v) => setForm({ ...form, home_or_away: v })}
              >
                <SelectTrigger data-testid="conv-home-away">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="home">Ospitante (in casa)</SelectItem>
                  <SelectItem value="away">Ospitata (in trasferta)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Convocati ({form.player_ids.length})</Label>
              <button
                type="button"
                className="text-xs text-sky-600 hover:underline"
                onClick={toggleAll}
              >
                {allSelected ? "Deseleziona tutti" : "Seleziona tutti"}
              </button>
            </div>
            <div className="border border-slate-200 rounded-xl max-h-60 overflow-y-auto">
              <PlayerCheckboxList
                players={sortedPlayers}
                selectedIds={form.player_ids}
                onToggle={togglePlayer}
              />
            </div>
          </div>

          <div>
            <Label>
              Dirigenti accompagnatori &amp; staff in distinta (
              {(form.accompagnatori_ids || []).length})
            </Label>
            <div
              className="border border-slate-200 rounded-xl max-h-60 overflow-y-auto"
              data-testid="conv-accomp-list"
            >
              {accompList.length === 0 ? (
                <div className="p-4 text-sm text-slate-500 text-center">
                  Aggiungi staff o genitori per poterli selezionare nella
                  distinta gara.
                </div>
              ) : (
                accompList.map((a) => (
                  <label
                    key={a.key}
                    className="flex items-center gap-3 p-2.5 hover:bg-slate-50 border-b border-slate-100 cursor-pointer"
                  >
                    <Checkbox
                      checked={(form.accompagnatori_ids || []).includes(a.key)}
                      onCheckedChange={() => toggleAccomp(a.key)}
                      data-testid={`conv-accomp-${a.key}`}
                    />
                    <span
                      className={`px-1.5 h-5 rounded text-[10px] font-bold flex items-center ${
                        a.kind === "staff"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-violet-100 text-violet-700"
                      }`}
                    >
                      {a.kind === "staff" ? "STAFF" : "FAM"}
                    </span>
                    <span className="text-sm flex-1">{a.name}</span>
                    <span className="text-xs text-slate-500">{a.sub}</span>
                    {a.tessera && (
                      <span className="text-[10px] font-mono text-slate-400">
                        {a.tessera}
                      </span>
                    )}
                  </label>
                ))
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button
            onClick={onSubmit}
            className="bg-sky-500 hover:bg-sky-600"
            data-testid="save-conv-btn"
          >
            Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
